import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    // Check for both customer and admin tokens
    const customerToken = localStorage.getItem('accessToken');
    const adminToken = localStorage.getItem('adminToken');
    const token = customerToken || adminToken;
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let refreshQueue = [];

// Helper to process queued requests after refresh
function processQueue(error, token = null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
}

// Add response interceptor to handle auth errors and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Prevent infinite loop: if the failing request is already the refresh endpoint, don't retry
    const isRefreshEndpoint = originalRequest.url?.includes('/auth/refresh') || 
                                originalRequest.url?.includes('/api/auth/refresh');
    
    const errorCode = error.response?.data?.code;
    if (error.response?.status === 401 &&
      errorCode === 'TOKEN_EXPIRED' && !originalRequest._retry && !isRefreshEndpoint) {
      originalRequest._retry = true;
      
      // Check if this is an admin request
      const adminToken = localStorage.getItem('adminToken');
      const customerRefreshToken = localStorage.getItem('refreshToken');
      
      if (adminToken) {
        // For admin requests, we don't have refresh token mechanism
        // Just clear admin data and redirect to admin login
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        window.location.href = '/login';
        return Promise.reject(error);
      } else if (customerRefreshToken) {
        // Only attempt refresh if this is NOT a logout request
        // For logout and other critical failures, redirect immediately
        const isLogoutRequest = originalRequest.url?.includes('/auth/logout');
        
        if (isLogoutRequest) {
          // Logout request failed - clear everything and redirect immediately
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userData');
          window.location.href = '/login';
          return Promise.reject(error);
        }
        
        // Attempt token refresh (single attempt, no retries for speed)
        try {
          if (isRefreshing) {
            // Wait for the ongoing refresh to complete
            return new Promise((resolve, reject) => {
              refreshQueue.push({ resolve, reject });
            }).then((newAccessToken) => {
              originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
              return apiClient(originalRequest);
            });
          }

          isRefreshing = true;
          const newAccessToken = await attemptTokenRefresh(originalRequest, customerRefreshToken);

          // Process queued requests
          processQueue(null, newAccessToken);
          isRefreshing = false;

          // Retry original request with new token
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          return apiClient(originalRequest);

        } catch (refreshError) {
          // Refresh failed: clear storage and logout
          processQueue(refreshError, null);
          isRefreshing = false;

          console.warn('[Auth] Token refresh failed, redirecting to login');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userData');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        console.warn('[Auth] No refresh token available, logging out');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userData');
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// Helper function to attempt token refresh (single attempt, no retries for speed)
async function attemptTokenRefresh(originalRequest, refreshToken) {
  try {
    // Use apiClient to ensure proper base URL and configuration
    const response = await apiClient.post('/auth/refresh', {
      refreshToken: refreshToken
    });
    
    const { accessToken, refreshToken: newRefreshToken } = response.data;
    
    // Update tokens in localStorage
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', newRefreshToken);
    
    console.log('[Auth] Token refresh successful');
    
    // Retry the original request with new token
    originalRequest.headers.Authorization = `Bearer ${accessToken}`;
    originalRequest._retry = false; // Reset retry flag for the retried request
    return apiClient(originalRequest);
    
  } catch (refreshError) {
    // Any refresh error means we should logout immediately
    // No retries to avoid delays
    const status = refreshError.response?.status;
    const errorMessage = refreshError.response?.data?.message || refreshError.message;
    
    console.warn('[Auth] Token refresh failed, redirecting immediately:', {
      status,
      message: errorMessage
    });
    
    // Clear storage and redirect immediately
    logoutUser();
    return Promise.reject(refreshError);
  }
}

// Helper function to logout user
function logoutUser() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userData');
  window.location.href = '/login';
}

export const authService = {
  // Customer login
  login: async (email, password) => {
    const response = await apiClient.post('/auth/login', { email, password });
    const { accessToken, refreshToken, ...userData } = response.data;
    
    // Store tokens and user data
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('userData', JSON.stringify(userData));
    
    return response.data;
  },

  // Refresh token
  refreshToken: async (refreshToken) => {
    const response = await apiClient.post('/auth/refresh', { refreshToken });
    const { accessToken, refreshToken: newRefreshToken } = response.data;
    
    // Update tokens in localStorage
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', newRefreshToken);
    
    return response.data;
  },

  // Logout - Non-blocking: clear storage immediately, fire API call in background
  logout: () => {
    // Clear local storage immediately
    const refreshToken = localStorage.getItem('refreshToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    
    // Fire logout API call in background (don't wait for it)
    if (refreshToken) {
      apiClient.post('/auth/logout', { refreshToken }).catch(error => {
        console.error('Error during logout API call (non-blocking):', error);
      });
    }
  },

  // Customer signup/onboarding
  signup: async (userData) => {
    const response = await apiClient.post('/customers/onboard', userData);
    return response.data;
  },

  // Forgot password
  forgotPassword: async (email) => {
    const response = await apiClient.post('/auth/password/reset', { email });
    return response.data;
  },

  // Reset password
  resetPassword: async (resetToken, newPassword) => {
    const response = await apiClient.post('/auth/password/reset/confirm', { 
      resetToken, 
      newPassword 
    });
    return response.data;
  },

  // Get current user profile
  getProfile: async () => {
    const response = await apiClient.get('/customers/profile');
    return response.data;
  },

  // Update profile
  updateProfile: async (profileData) => {
    const response = await apiClient.put('/customers/profile', profileData);
    return response.data;
  },

  // Change password
  changePassword: async (currentPassword, newPassword) => {
    const response = await apiClient.post('/auth/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  },

  // Verify email
  verifyEmail: async (token) => {
    const response = await apiClient.post('/email-verification/verify', { token });
    return response.data;
  },

  // Resend verification email (admin only)
  resendVerificationEmail: async (customerId) => {
    const response = await apiClient.post(`/email-verification/resend/${customerId}`);
    return response.data;
  }
};

export default apiClient;
